import { getOtherBrokerGroups } from '../api/usersApi';

/** The user fields the edit drawer changes, in the order the save confirmation lists them. */
export const USER_FIELD_LABELS = {
	imageUrl: 'Photo',
	name: 'Name',
	email: 'Email',
	phoneNumber: 'Phone',
	role: 'Role',
	brokerGroupId: 'Primary broker group',
	brokerGroupArray: 'Broker groups',
	salesTeamId: 'Sales team id',
	serviceTeamId: 'Service team id',
	accountTypeId: 'Account type id',
	authorisedRepId: 'Authorised rep id',
	canViewGroup: 'Can view group',
	principleUser: 'Principal user',
	isNetworkAdmin: 'Network admin',
	isFolioAdmin: 'Folio admin',
	complianceManager: 'Compliance manager',
};

/** Permission fields, shown as checkboxes; unset counts as false. */
export const USER_PERMISSION_FIELDS = [
	'canViewGroup',
	'principleUser',
	'isNetworkAdmin',
	'isFolioAdmin',
	'complianceManager',
];

/**
 * The edit drawer's starting values for a users list item. featureFlags are the ids of the
 * flags that target the user directly (from listAllFeatureFlags); the form edits them as a
 * draft and Save adds or removes the user on each changed flag. A new photo is photoBlob
 * (cropped, not yet uploaded), with imageUrl its data URL until Save uploads it.
 */
export const getUserFormValues = (user, flags) => ({
	imageUrl: user.imageUrl ?? '',
	photoBlob: null,
	name: user.name ?? '',
	email: user.email ?? '',
	phoneNumber: user.phoneNumber ?? '',
	role: user.role ?? '',
	brokerGroupId: user.primaryBrokerGroupId ?? '',
	brokerGroupArray: getOtherBrokerGroups(user).map(({ id }) => id),
	salesTeamId: user.salesTeamId ?? '',
	serviceTeamId: user.serviceTeamId ?? '',
	accountTypeId: user.accountTypeId ?? '',
	authorisedRepId: user.authorisedRepId ?? '',
	...Object.fromEntries(USER_PERMISSION_FIELDS.map((field) => [field, Boolean(user[field])])),
	featureFlags: flags.filter((flag) => (flag.users ?? []).includes(user.id)).map((flag) => flag.id),
});

/** The new user drawer's starting values: empty, with every permission off. */
export const getNewUserFormValues = () => ({
	imageUrl: '',
	photoBlob: null,
	name: '',
	email: '',
	phoneNumber: '',
	role: '',
	brokerGroupId: '',
	brokerGroupArray: [],
	salesTeamId: '',
	serviceTeamId: '',
	accountTypeId: '',
	authorisedRepId: '',
	...Object.fromEntries(USER_PERMISSION_FIELDS.map((field) => [field, false])),
	featureFlags: [],
});

/**
 * A new user's fields, cast by the validation schema (trimmed, empty ids as null): `changed`
 * lists the ones that are set and `after` their values, for the save confirmation, and
 * `body` is the create request. The photo isn't in the body: it's uploaded once the user
 * exists, into their own storage folder.
 */
export const getNewUserChanges = (validationSchema, values) => {
	const after = validationSchema.cast(values);
	const changed = Object.keys(USER_FIELD_LABELS).filter((field) =>
		Array.isArray(after[field]) ? after[field].length : Boolean(after[field]),
	);
	const body = Object.fromEntries(
		Object.keys(USER_FIELD_LABELS)
			.filter((field) => field !== 'imageUrl')
			.map((field) => [field, after[field]]),
	);
	return { changed, after, body };
};

const isSame = (a, b) =>
	Array.isArray(a) || Array.isArray(b)
		? [...(a ?? [])].sort().join() === [...(b ?? [])].sort().join()
		: (a ?? null) === (b ?? null);

/**
 * What the admin changed, from the form's starting and current values cast by the
 * validation schema (trimmed, empty ids as null): `changed` lists the fields, `before` and
 * `after` their values, and `body` is the update request. The primary broker group is also
 * sent when the user's stored `brokerGroup` and `brokerGroupId` disagree, so saving makes
 * them match.
 */
export const getUserChanges = (validationSchema, initialValues, values, user) => {
	const before = validationSchema.cast(initialValues, { assert: false });
	const after = validationSchema.cast(values);
	const changed = Object.keys(USER_FIELD_LABELS).filter(
		(field) => !isSame(before[field], after[field]),
	);
	const fixesPrimary =
		user.brokerGroup && user.brokerGroupId && user.brokerGroup !== user.brokerGroupId;
	const body = Object.fromEntries([
		...changed.map((field) => [field, after[field]]),
		...(fixesPrimary ? [['brokerGroupId', after.brokerGroupId]] : []),
	]);
	return { changed, before, after, body };
};

/** The feature flag ids the user is being added to and removed from. */
export const getFeatureFlagChanges = (initialValues, values) => ({
	added: values.featureFlags.filter((id) => !initialValues.featureFlags.includes(id)),
	removed: initialValues.featureFlags.filter((id) => !values.featureFlags.includes(id)),
});
